import React from "react";
import { Trans } from "@lingui/macro";
import { getColor } from "components/utils";
import { Alert } from "components/UI/Alert/Alert";
import { useDispatch, useSelector } from "react-redux";
import { DotsVerticalIcon } from "@heroicons/react/outline";
import { ButtonLink } from "components/UI/ButtonLink/ButtonLink";
import { IdOrdersState } from "model/dashboardOrders/dOrdersReducer";
import { InputCheckbox } from "components/UI/InputCheckbox/InputCheckbox";
import { typeDataProductInfoBulkActions } from "components/containers/dashboard/dataProducts";
import {
  getColorOrders,
  getTransStatus,
  notEditedStatus,
} from "constants/ordersStatus";
import {
  dOrdersCheckedItemAC,
  dOrdersSetDetailItemAC,
  dOrdersCheckedAllItemAC,
} from "model/dashboardOrders/dOrdersActionCreators";
import dayjs from "dayjs";

// Type
export type typeTableHeader = {
  variant?: string;
  bulkOperations: typeDataProductInfoBulkActions;
};

// Header data component
export const TableHeader: React.FC<typeTableHeader> = ({
  variant,
  bulkOperations,
}) => {
  const color = getColor(variant);

  const dispatch = useDispatch();

  const tableData = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.data,
  );

  const groupActions = tableData.find((item) => item.checked);

  const checkedAll = () => {
    dispatch(dOrdersCheckedAllItemAC(true));
  };

  const unCheckedAll = () => {
    dispatch(dOrdersCheckedAllItemAC(false));
  };

  return (
    <>
      {!groupActions ? (
        <>
          <th
            scope="col"
            className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-center"
          >
            <InputCheckbox
              id="selectAll"
              checked={false}
              onChange={() => checkedAll()}
            />
          </th>
          <th
            scope="col"
            className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
          >
            <div className="flex flex-col">
              <span className="border-b">
                <Trans>Orders number</Trans>
              </span>
              <span>
                <Trans>Date</Trans>
              </span>
            </div>
          </th>
          <th
            scope="col"
            className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
          >
            <div className="flex flex-col">
              <span className="border-b">
                <Trans>Receipt number</Trans>
              </span>
              <span>
                <Trans>Date of sale</Trans>
              </span>
            </div>
          </th>
          <th
            scope="col"
            className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
          >
            <span className="whitespace-nowrap">
              <Trans>External order number</Trans>
            </span>
          </th>
          <th
            scope="col"
            className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
          >
            <span className="whitespace-nowrap">
              <Trans>Payment method</Trans>
            </span>
          </th>
          <th
            scope="col"
            className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
          >
            <Trans>Client</Trans>
          </th>
          <th
            scope="col"
            className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
          >
            <Trans>Courier</Trans>
          </th>
          <th
            scope="col"
            className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
          >
            <Trans>Total-Total</Trans>
          </th>

          <th
            scope="col"
            className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
          >
            <Trans>Status</Trans>
          </th>
          <th scope="col" className="w-1"></th>
        </>
      ) : (
        <>
          <th
            scope="col"
            className=" px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center cursor-pointer"
          >
            <div className="flex justify-center" onClick={() => unCheckedAll()}>
              <div
                className={`flex flex-col justify-center items-center h-4 w-4 focus:ring-white border border-gray-100 rounded bg-${color}-600`}
              >
                <div className="h-0.5 w-2 my-1 bg-white rounded"></div>
              </div>
            </div>
          </th>
          <th colSpan={10} className="py-3 text-sm">
            <div className="flex divide-x divide-gray-300">
              {bulkOperations.map((item, index) => (
                <ButtonLink
                  key={index}
                  dark={true}
                  type="button"
                  label={item.title}
                  onClick={() => item.action()}
                  buttonClassName="px-3 font-medium text-gray-700"
                />
              ))}
            </div>
          </th>
        </>
      )}
    </>
  );
};

// Type
export type typeAction = {
  id: string;
  title: JSX.Element;
  disableSwitcher?: boolean;
  action: (id: string) => void;
};

export type typeTableBody = {
  variant?: string;
  rowsActions: typeAction[];
  setOpenPopupDetails: React.Dispatch<React.SetStateAction<boolean>>;
};

// Body data component
export const TableBody: React.FC<typeTableBody> = ({
  variant,
  rowsActions,
  setOpenPopupDetails,
}) => {
  const color = getColor(variant);

  const dispatch = useDispatch();

  const tableData = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.data,
  );

  const onCellClickHandler = (
    e: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>,
    id: string,
  ) => {
    dispatch(dOrdersSetDetailItemAC(id));
    setOpenPopupDetails(true);
  };

  const onChangeSelectItemHandler = (id: string) => {
    dispatch(dOrdersCheckedItemAC(id));
  };

  let component;

  if (tableData.length) {
    component = tableData.map((item, index) => {
      const payments = item?.sale?.payments || [];
      const statusColor = getColorOrders(item.status);

      let date = "-";
      if (item.dateCreated) {
        date = dayjs(item.dateCreated).format("YYYY-MM-DD, HH:mm");
      }

      let dateOfSale = "-";
      if (item?.sale?.date) {
        dateOfSale = dayjs(item.sale.date).format("YYYY-MM-DD, HH:mm");
      }

      const transStatus = getTransStatus(item.status!);

      return (
        <tr
          key={item.id}
          className={`${
            index % 2 === 0 ? "bg-white" : "bg-gray-50"
          } hover:bg-${color}-50`}
        >
          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
            <div className="flex justify-center">
              <InputCheckbox
                id={"select-" + item.id}
                checked={item.checked}
                onChange={() => onChangeSelectItemHandler(item.id)}
              />
            </div>
          </td>
          <td
            className="px-4 py-3 whitespace-nowrap w-1/4 text-sm font-sm text-gray-500 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            <div className="flex flex-col">
              <span className={"text-gray-900 font-normal border-b"}>
                {item.number ? item.number : ""}
              </span>
              <div>
                <span className="text-gray-500 text-xs leading-4  font-normal">
                  {date}
                </span>
              </div>
            </div>
          </td>
          <td
            className="px-4 py-3 whitespace-nowrap w-1/4 text-sm font-sm text-gray-500 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            <div className="flex flex-col">
              <span className="text-gray-900 font-normal border-b">
                {item?.sale?.receiptNumber ?? "-"}
              </span>
              <div>
                <span className="text-gray-500 text-xs leading-4 font-normal">
                  {dateOfSale ?? "-"}
                </span>
              </div>
            </div>
          </td>
          <td
            className="px-4 py-3 whitespace-wrap text-sm font-normal leading-5 text-gray-900 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            <p className="truncate max-w-2xl">{item.externalId || "-"}</p>
          </td>
          <td
            className="px-4 py-3 whitespace-wrap text-sm font-normal leading-5 text-gray-900 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            {payments.length
              ? payments.map((payment, index) => {
                  return (
                    <div key={index}>
                      <span
                        className={`text-gray-900 font-normal ${
                          index + 1 < payments.length ? "border-b" : ""
                        }`}
                      >
                        {payment.paymentType}
                      </span>
                    </div>
                  );
                })
              : "-"}
          </td>
          <td
            className="px-4 py-3 whitespace-wrap text-sm font-normal leading-5 text-gray-900 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            {item.customerName}
          </td>
          <td
            className="px-4 py-3 whitespace-wrap text-sm font-normal leading-5 text-gray-900 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            {item.courier ? item.courier.name : "-"}
          </td>
          <td
            className="px-4 py-3 whitespace-wrap text-sm font-normal leading-5 text-gray-900 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            {item?.sale?.amount ?? "-"}
          </td>
          <td
            className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            <div className="flex ">
              <span
                className={` bg-${statusColor} rounded w-2 h-2 mr-2 self-center`}
              />
              <span className={""}>{transStatus}</span>
            </div>
          </td>
          <td className="whitespace-nowrap text-sm text-gray-500 pr-2">
            <div className="relative flex">
              {!notEditedStatus.includes(item.status) && (
                <div className=" row-action-block">
                  <DotsVerticalIcon className="row-action-list-open-button h-5 w-5 text-gray-900" />
                  <div className="row-action-list">
                    <div className="flex gap-x-4">
                      {rowsActions.map((actionItem, ActionIndex) => {
                        if (actionItem.id === "edit") {
                          if (!notEditedStatus.includes(item.status)) {
                            return (
                              <ButtonLink
                                key={ActionIndex}
                                type={"button"}
                                label={actionItem.title}
                                onClick={() => actionItem.action(item.id)}
                              />
                            );
                          }

                          return null;
                        } else {
                          return (
                            <ButtonLink
                              key={ActionIndex}
                              type={"button"}
                              label={actionItem.title}
                              onClick={() => actionItem.action(item.id)}
                            />
                          );
                        }
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      );
    });
  } else {
    component = (
      <tr>
        <td className="p-2" colSpan={10}>
          <Alert
            className="w-full"
            type={"info"}
            title={
              <Trans>
                The Orders list is empty. Click Add button to create order or
                check filters.
              </Trans>
            }
          />
        </td>
      </tr>
    );
  }

  return <>{component}</>;
};
